import type { LocalDate, TimeZone, Uuid } from "@planerius/contracts"
import type { CancelReasonView, ScheduleResult } from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"

/**
 * Журнал дня.
 *
 * Ответ НЕ проверяется схемой намеренно: ScheduleResult — самый большой ответ кабинета
 * (день на пятнадцать мастеров это тысячи строк занятости), и разбирать его Zod на каждом
 * перелистывании даты значит подарить человеку заметную задержку ради проверки, которая
 * в бою всё равно ничего не чинит. Типы при этом настоящие, из контрактов.
 */

/**
 * Параметры запроса журнала.
 *
 * branchId обязателен — режима «все точки» у журнала нет и быть не может: у каждого филиала
 * своя зона, свой шаг сетки и свои часы, и день двух филиалов в одной сетке нечитаем.
 * Границы периода — календарные даты филиала, а не моменты: «16 сентября» разворачивает
 * в интервал абсолютного времени сервер, по зоне филиала.
 */
export interface JournalQuery {
  readonly branchId: Uuid
  readonly dateFrom: LocalDate
  readonly dateTo: LocalDate
  /** Пусто = все мастера со сменой. Фильтр может только СУЗИТЬ выдачу, не расширить. */
  readonly employeeIds?: readonly Uuid[] | undefined
  readonly includeCancelled?: boolean | undefined
  /** Только для подписи «у вас сейчас 14:40». В расчёте сетки не участвует. */
  readonly viewerTimeZone?: TimeZone | undefined
}

export function fetchJournal(query: JournalQuery, signal?: AbortSignal): Promise<ScheduleResult> {
  return apiRequest<ScheduleResult>("/cabinet/journal", {
    query: {
      branchId: query.branchId,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      employeeIds: query.employeeIds,
      includeCancelled: query.includeCancelled,
      viewerTimeZone: query.viewerTimeZone,
    },
    signal,
  })
}

export interface CancelReasonsQuery {
  readonly actor?: "client" | "staff" | "system" | undefined
  readonly includeInactive?: boolean | undefined
}

/**
 * Причины отмены. Берутся вместе с журналом, а не в момент открытия диалога отмены:
 * администратор уже нажал «отменить визит», и ждать ещё один запрос ему негде.
 */
export function fetchCancelReasons(
  query: CancelReasonsQuery = {},
  signal?: AbortSignal,
): Promise<CancelReasonView[]> {
  return apiRequest<CancelReasonView[]>("/cabinet/journal/cancel-reasons", {
    query: { actor: query.actor, includeInactive: query.includeInactive },
    signal,
  })
}
